import axios from 'axios';
import { noop } from '../lib/serviceHelpers';
import { useMutation, useQuery } from '@tanstack/react-query';

// Operations > Leads queue. Filters: status, group, assignedTo, overdue, search,
// sortBy, sortDir, page, limit.
export const useFetchLeads = (params, opts = {}) => {
  return useQuery(
    ['leads', params],
    async () => {
      const res = await axios.get(`/leads`, { params });
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
      ...opts,
    },
  );
};

// Live "Today's New Leads" count (Central time). Polls so the counter + new-lead
// toast update on their own. Returns { count, latest: { leadId, name } | null }.
export const useFetchTodayLeadCount = (opts = {}) => {
  return useQuery(
    ['leads-today-count'],
    async () => {
      const res = await axios.get(`/leads/today-count`);
      return res.data;
    },
    {
      refetchInterval: 20000,
      refetchOnWindowFocus: true,
      staleTime: 10000,
      ...opts,
    },
  );
};

// Dashboard tab analytics. keepPreviousData keeps the old charts rendered
// (with a shimmer) while a new range loads — no layout flash. No polling; an
// analytics view only needs focus refetch.
export const useFetchLeadsDashboard = (params, opts = {}) => {
  return useQuery(
    ['leads-dashboard', params],
    async () => {
      const res = await axios.get(`/leads/dashboard`, { params });
      return res.data;
    },
    {
      keepPreviousData: true,
      staleTime: 30000,
      refetchOnWindowFocus: true,
      // Don't retry 4xx (e.g. range-too-large) — only transient server errors.
      retry: (failureCount, err) => {
        const status = err?.response?.status;
        return (!status || status >= 500) && failureCount < 2;
      },
      ...opts,
    },
  );
};

// Raw lead-level rows behind the dashboard (the "show your work" CSV).
export const fetchLeadsDashboardExport = async (params) => {
  const res = await axios.get(`/leads/dashboard/export`, { params });
  return res.data;
};

// Full lead detail: lead + veteranSnapshot + outreach[] + events[].
export const useFetchLead = (leadId, opts = {}) => {
  return useQuery(
    ['lead', leadId],
    async () => {
      const res = await axios.get(`/leads/${leadId}`);
      return res.data;
    },
    {
      enabled: !!leadId,
      refetchOnWindowFocus: false,
      ...opts,
    },
  );
};

export const useSetLeadStatus = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId, status }) => {
      const res = await axios.patch(`/leads/${leadId}/status`, { status });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useLogOutreach = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId, ...payload }) => {
      const res = await axios.post(`/leads/${leadId}/outreach`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Send a text to a lead AS the acting user — the browser passes the user's
// RingCentral OAuth token so the backend sends from their own number.
export const useSendLeadText = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId, text, rcAccessToken }) => {
      const res = await axios.post(`/leads/${leadId}/text`, {
        text,
        rcAccessToken,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Resolve the acting user's own RingCentral SMS number (the Text composer's
// "From"). Returns { fromNumber } — fromNumber is null when they have no
// SMS-enabled number.
export const useMySmsNumber = (opts = {}) => {
  return useMutation(async ({ rcAccessToken }) => {
    const res = await axios.post(`/leads/rc/sms-number`, { rcAccessToken });
    return res.data;
  }, opts);
};

// useCreateLead/useCreatePublicLead DELETED 2026-08-26 with the legacy
// /leads page (POST /api/public/leads is gone; /reach-out replaces it).

// Public /reach-out self-lead form → creates a Website-channel lead.
export const useCreateReachOutLead = (callback, opts = {}) => {
  return useMutation(
    async (payload) => {
      const res = await axios.post(`/public/reach-out`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Public event QR landing (/event/:slug) → creates a lead under the event.
export const useCreateEventLead = (callback, opts = {}) => {
  return useMutation(
    async ({ slug, payload }) => {
      const res = await axios.post(`/public/events/${slug}/leads`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Operations > Leads > Events tab — event lead channels (partners rows of
// type 'event'), managed under leads permissions.
export const useFetchEvents = (params, opts = {}) => {
  return useQuery(
    ['leads-events', params],
    async () => {
      const res = await axios.get(`/leads/events`, { params });
      return res.data;
    },
    { refetchOnWindowFocus: false, staleTime: 15000, ...opts },
  );
};

export const useCreateEvent = (callback, opts = {}) => {
  return useMutation(
    async (payload) => {
      const res = await axios.post(`/leads/events`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUpdateEvent = (callback, opts = {}) => {
  return useMutation(
    async ({ partnerId, ...payload }) => {
      const res = await axios.patch(`/leads/events/${partnerId}`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Public "Refer a Veteran" form → creates a Referral Program lead on the board.
export const useCreateReferral = (callback, opts = {}) => {
  return useMutation(
    async (payload) => {
      const res = await axios.post(`/public/referrals`, { ...payload });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Public "Become a Partner" form (no referral) → mints an onboarding token and
// returns { onboarding: { url, token } } to drop them into /partner-onboarding.
export const useCreatePartnerSignup = (callback, opts = {}) => {
  return useMutation(
    async (payload) => {
      const res = await axios.post(`/public/partner-signup`, { ...payload });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Referrer self-onboarding (tokenized, public): intro + start the e-sign session.
export const useFetchOnboarding = (token) =>
  useQuery(
    ['partner-onboarding', token],
    async () => (await axios.get(`/public/partner-onboarding/${token}`)).data,
    { enabled: Boolean(token), retry: false },
  );

export const useStartOnboarding = () =>
  useMutation(async ({ token, email }) => {
    const res = await axios.post(`/public/partner-onboarding/${token}/start`, {
      email,
    });
    return res.data;
  });

// Expired-link recovery: server verifies the token's HMAC (ignoring expiry),
// re-mints a fresh token, and re-emails the onboarding link.
export const useResendOnboardingLink = () =>
  useMutation(async ({ token, email }) => {
    const res = await axios.post(`/public/partner-onboarding/resend`, {
      token,
      email,
    });
    return res.data;
  });

// Partner phone OTP — gates onboarding (the phone rides in the token).
export const useSendOnboardingPhoneCode = () =>
  useMutation(async (token) => {
    const res = await axios.post(
      `/public/partner-onboarding/${token}/send-phone-code`,
    );
    return res.data;
  });

export const useVerifyOnboardingPhoneCode = () =>
  useMutation(async ({ token, code }) => {
    const res = await axios.post(
      `/public/partner-onboarding/${token}/verify-phone-code`,
      { code },
    );
    return res.data;
  });

export const useUpdateLead = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId, payload }) => {
      const res = await axios.put(`/leads/${leadId}`, {
        ...payload,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useDeleteLead = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId, reason, reasonDetail }) => {
      // reasonDetail: Other's few typed words (Nic 2026-09-30).
      const res = await axios.delete(`/leads/${leadId}`, {
        data: { reason, ...(reasonDetail ? { reasonDetail } : {}) },
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useSetLeadContacted = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId, contacted, at }) => {
      const res = await axios.patch(`/leads/${leadId}/contacted`, {
        contacted,
        at,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useSetLeadFollowUp = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId, scheduled, at }) => {
      const res = await axios.patch(`/leads/${leadId}/follow-up`, {
        scheduled,
        at,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useCompleteLeadFollowUp = (callback, opts = {}) => {
  return useMutation(
    async ({ leadId }) => {
      const res = await axios.patch(`/leads/${leadId}/follow-up/complete`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Lead call transcripts (2026-08-18). `endpointBase` is the calling
// surface's own gated route family ('/onboarding-board/leads' on the board;
// the Client Workspace has its own client route). The query
// key deliberately lives OUTSIDE the ['onboarding-board'] prefix so the
// board's SSE invalidations never hammer RC through a closed card.
export const useLeadTranscripts = (endpointBase, leadId, opts = {}) => {
  return useQuery(
    ['lead-transcripts', leadId],
    async () => {
      const res = await axios.get(`${endpointBase}/${leadId}/transcripts`);
      return res.data;
    },
    { enabled: !!leadId, staleTime: 30000, ...opts },
  );
};

export const useRefreshLeadTranscripts = (
  endpointBase,
  callback,
  opts = {},
) => {
  return useMutation(
    async ({ leadId }) => {
      const res = await axios.post(
        `${endpointBase}/${leadId}/transcripts/refresh`,
      );
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};
